import {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@saay/shadcn/components/card';
import { cn } from '@saay/shadcn/lib/utils';
import type { ComponentProps, ReactNode } from 'react';

import { IconTile, type Tone } from './tone.js';
import type { LucideIcon } from 'lucide-react';

/**
 * The soft-grey surface every block sits on: a shadcn Card without the ring, with the v2 radius
 * and bold titles. Use the Card parts inside it (CardHeader, CardTitle, CardDescription,
 * CardAction, CardContent, CardFooter).
 */
export function Surface({ className, ...props }: ComponentProps<typeof Card>) {
  return (
    <Card
      className={cn(
        // v2 surfaces carry no lines: the footer drops the stock border and tint.
        'gap-4 rounded-3xl py-5 ring-0 [--card-spacing:--spacing(5)] [&_[data-slot=card-title]]:font-bold [&_[data-slot=card-footer]]:border-0 [&_[data-slot=card-footer]]:bg-transparent [&_[data-slot=card-footer]]:pt-0',
        className,
      )}
      {...props}
    />
  );
}

/** A section heading outside a surface (a list title with an optional «همه» action). */
export function SectionHeader({
  title,
  hint,
  action,
  className,
}: {
  readonly title: string;
  readonly hint?: string;
  readonly action?: ReactNode;
  readonly className?: string;
}) {
  return (
    <div className={cn('flex items-end justify-between gap-3', className)}>
      <div>
        <h2 className="text-base font-bold">{title}</h2>
        {hint === undefined ? null : <p className="mt-0.5 text-xs text-muted-foreground">{hint}</p>}
      </div>
      {action}
    </div>
  );
}

/** One figure on a surface: label and icon tile, the value, then one muted foot line. */
export function KpiCard({
  label,
  icon,
  tone,
  value,
  foot,
  className,
}: {
  readonly label: string;
  readonly icon: LucideIcon;
  readonly tone: Tone;
  readonly value: ReactNode;
  readonly foot?: ReactNode;
  readonly className?: string;
}) {
  return (
    <Surface className={className}>
      <CardHeader>
        <CardDescription>{label}</CardDescription>
        <CardAction>
          <IconTile icon={icon} size="sm" tone={tone} />
        </CardAction>
      </CardHeader>
      <CardContent className="mt-auto">{value}</CardContent>
      {foot === undefined ? null : (
        <CardFooter className="text-xs text-muted-foreground [&_[data-slot=progress-track]]:bg-background">
          {foot}
        </CardFooter>
      )}
    </Surface>
  );
}

export { CardAction, CardContent, CardDescription, CardFooter, CardHeader, CardTitle };
